import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { InputNumberModule } from 'primeng/inputnumber';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of } from 'rxjs';
import {
  CreateProductTaxRequest,
  ProductTaxDTO,
  ProductTaxService,
  UpdateProductTaxRequest
} from '../../services/product-tax.service';
import { TaxDTO, TaxService } from '../../services/tax.service';
import { EntityAuditPanelComponent } from '../shared/entity-audit/entity-audit-panel.component';

@Component({
  selector: 'app-product-tax-form',
  templateUrl: './product-tax-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputNumberModule,
    FloatLabelModule,
    SelectModule,
    SelectButtonModule,
    ButtonModule,
    CardModule,
    TabsModule,
    TooltipModule,
    EntityAuditPanelComponent
  ]
})
export class ProductTaxFormComponent {
  private fb = inject(FormBuilder);
  private productTaxService = inject(ProductTaxService);
  private taxService = inject(TaxService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);

  form: FormGroup;
  loading = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  editingId = signal<number | null>(null);
  taxOptions = signal<TaxDTO[]>([]);

  taxApplicationOptions = [
    { value: 'TAXABLE', label: 'Gravado' },
    { value: 'EXEMPT', label: 'Exento' },
    { value: 'ZERO_RATED', label: 'Tasa 0%' }
  ];

  taxCollectionTypeOptions = [
    { value: 'SELLER_COLLECTS', label: 'Traslado' },
    { value: 'SELLER_WITHHOLDS', label: 'Retención' }
  ];

  activeTab = signal<string>('general');
  tabs = [
    { value: 'general', label: 'General', icon: 'pi pi-percentage' },
    { value: 'auditoria', label: 'Auditoría', icon: 'pi pi-history' }
  ] as const;

  constructor() {
    this.form = this.fb.group({
      taxSelection: [null, Validators.required],
      taxRate: [null, [Validators.min(0), Validators.max(100)]],
      taxAmount: [null, [Validators.min(0)]],
      applicationType: ['TAXABLE', Validators.required],
      collectionType: ['SELLER_COLLECTS']
    });

    const taxRateCtrl = this.form.get('taxRate');
    const taxAmountCtrl = this.form.get('taxAmount');
    taxRateCtrl?.valueChanges.subscribe(value => {
      if (value !== null && value !== undefined && value !== '') {
        taxAmountCtrl?.setValue(null, { emitEvent: false });
      }
    });
    taxAmountCtrl?.valueChanges.subscribe(value => {
      if (value !== null && value !== undefined && value !== '') {
        taxRateCtrl?.setValue(null, { emitEvent: false });
      }
    });

    this.loadAllTaxes();

    this.route.paramMap.subscribe(params => {
      const routeId = params.get('id');
      if (routeId) {
        const id = Number(routeId);
        if (!Number.isNaN(id)) {
          this.isEditMode.set(true);
          this.editingId.set(id);
          this.loadProductTax(id);
          return;
        }
      }
      this.isEditMode.set(false);
      this.editingId.set(null);
      this.form.reset({
        taxSelection: null,
        taxRate: null,
        taxAmount: null,
        applicationType: 'TAXABLE',
        collectionType: 'SELLER_COLLECTS'
      });
    });
  }

  onSubmit(): void {
    if (this.form.invalid || !this.isTaxValueValid()) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const payload = this.buildRequest();
    const isEdit = this.isEditMode() && this.editingId() !== null;
    const request$ = isEdit
      ? this.productTaxService.updateProductTax(this.editingId() as number, payload)
      : this.productTaxService.createProductTax(payload);

    request$.pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (response) => {
        if (response.ok) {
          this.toastr.success(isEdit ? 'Impuesto actualizado correctamente' : 'Impuesto creado correctamente');
          const id = response.body?.id ?? this.editingId();
          if (id) {
            this.router.navigate(['/product-taxes', id], { replaceUrl: true });
            this.isEditMode.set(true);
            this.editingId.set(id);
          } else {
            this.router.navigate(['/product-taxes']);
          }
        } else {
          this.setError(response.message || 'Error al guardar el impuesto');
        }
      },
      error: (err) => {
        this.setError(err.error?.message || 'Error al guardar el impuesto');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/product-taxes']);
  }

  onTabChange(value: string | number | undefined): void {
    this.activeTab.set(String(value ?? 'general'));
  }

  private loadProductTax(id: number): void {
    this.loading.set(true);
    this.productTaxService.getProductTaxById(id).subscribe({
      next: (response) => {
        if (response.ok) {
          this.patchForm(response.body);
        } else {
          this.setError(response.message || 'Error al cargar el impuesto');
        }
        this.loading.set(false);
      },
      error: () => {
        this.setError('Error al cargar el impuesto');
        this.loading.set(false);
      }
    });
  }

  private patchForm(item: ProductTaxDTO): void {
    const tax = item.tax;
    if (tax) {
      this.addTaxOption(tax);
    }
    this.form.patchValue({
      taxSelection: tax ?? null,
      taxRate: item.taxRate ?? null,
      taxAmount: item.taxAmount ?? null,
      applicationType: item.applicationType || 'TAXABLE',
      collectionType: item.collectionType || 'SELLER_COLLECTS'
    });
    if (this.isEditMode()) {
      this.form.get('taxSelection')?.disable();
    }
  }

  private buildRequest(): CreateProductTaxRequest | UpdateProductTaxRequest {
    const tax = this.form.get('taxSelection')?.value as TaxDTO | null;
    return {
      taxId: tax?.id as number,
      taxRate: this.toNumberOrUndefined(this.form.get('taxRate')?.value),
      taxAmount: this.toNumberOrUndefined(this.form.get('taxAmount')?.value),
      applicationType: this.form.get('applicationType')?.value,
      collectionType: this.form.get('collectionType')?.value
    };
  }

  private loadAllTaxes(page: number = 0, acc: TaxDTO[] = []): void {
    this.taxService.listTaxes({}, { page, size: 200, sort: ['taxKey,asc'] })
      .pipe(catchError(() => of({ ok: false, body: [] as TaxDTO[], page: undefined })))
      .subscribe(response => {
        if (!response.ok) {
          this.taxOptions.set([]);
          return;
        }
        const merged = [...acc, ...response.body];
        if (response.page && response.page.currentPage < response.page.totalPages) {
          this.loadAllTaxes(response.page.currentPage, merged);
          return;
        }
        this.taxOptions.set(merged);
      });
  }

  private addTaxOption(option: TaxDTO): void {
    if (!this.taxOptions().some(item => item.id === option.id)) {
      this.taxOptions.set([option, ...this.taxOptions()]);
    }
  }

  private toNumberOrUndefined(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
  }

  private isTaxValueValid(): boolean {
    const rateValue = this.form.get('taxRate')?.value;
    const amountValue = this.form.get('taxAmount')?.value;
    const hasRate = rateValue !== null && rateValue !== undefined && rateValue !== '';
    const hasAmount = amountValue !== null && amountValue !== undefined && amountValue !== '';
    if (hasRate && hasAmount) {
      this.setError('Captura solo tasa o monto, no ambos.');
      return false;
    }
    if (!hasRate && !hasAmount) {
      this.setError('Captura al menos tasa o monto.');
      return false;
    }
    return true;
  }

  private setError(message: string): void {
    this.error.set(message);
    this.toastr.error(message);
  }
}
